import type * as React from "react";

import { cn } from "../lib/cn";

/** The one "stopped" paper stock (espresso ink ~11.7:1 on it); reuse rather than mix your own. */
const slipPaper =
  "bg-[color-mix(in_srgb,var(--color-wash-stopped)_40%,var(--color-card))]";

// Period = the 1.5em line-height; override font-size and leading together or not at all.
const SLIP_RULING = [
  "[background-image:repeating-linear-gradient(transparent,transparent_calc(1.5em_-_1px),color-mix(in_srgb,var(--color-ink-stopped)_40%,var(--color-card))_calc(1.5em_-_1px),color-mix(in_srgb,var(--color-ink-stopped)_40%,var(--color-card))_1.5em)]",
  "[background-size:100%_1.5em]",
].join(" ");

export interface MessageSlipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role"
> {
  message: React.ReactNode;
  onDismiss: () => void;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Persistent failure notice; the whole slip is one dismiss button. The caller
 * positions it, shows one at a time, and remounts with a fresh `key` per
 * message so the `role="alert"` re-announces.
 */
export function MessageSlip({
  message,
  onDismiss,
  className,
  ...rest
}: MessageSlipProps) {
  return (
    <div {...rest} role="alert" className={className}>
      <button
        type="button"
        onClick={onDismiss}
        className={cn(
          "block w-72 px-3.5 pt-2.5 pb-2 text-left",
          slipPaper,
          "shadow-lift-lg",
          // `backwards`, not `both`: filling forwards would pin translate and eat the hover lift.
          "[animation:bds-slip-in_220ms_var(--ease-paper)_backwards] motion-reduce:animate-none",
          "transition duration-150 ease-paper motion-reduce:transition-none",
          "hover:-translate-y-px hover:shadow-float",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        )}
      >
        <span aria-hidden="true" className="block">
          <span className="block text-center text-annotation-caps font-semibold text-ink-stopped">
            While you were out
          </span>
          <span className="mt-1.5 block h-1 border-y border-ink-stopped/40" />
        </span>

        <span
          className={cn(
            "block min-h-12 pt-1 pb-0.5 font-body text-[0.875rem] leading-[1.5em] text-ink",
            SLIP_RULING,
          )}
        >
          {message}
        </span>

        <span
          aria-hidden="true"
          className="mt-1 block text-right font-mono text-[0.625rem] tracking-[0.08em] text-annotation"
        >
          - Brian
        </span>

        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  );
}
